// EL REPASO DE LA LISTA DE URGENCIAS — la lista y las dos decisiones.
//
// Vive en un componente aparte porque se usa en DOS sitios que tienen que comportarse igual: la
// página /urgencias y el modal que salta el día del repaso. Si fueran dos copias, el día que se
// añada una columna o cambie una regla, una de las dos se quedaría atrás.
//
// Cada doctor solo ve a SUS pacientes. Eso NO se decide aquí: la lista viene ya filtrada del
// servidor y el intento de decidir sobre un paciente ajeno se rechaza allí. Esta pantalla se limita
// a pintar lo que le llega.
//
// EN MÓVIL Y TABLET NO HAY TABLA. Seis columnas con dos botones de decisión no caben en 768 px: se
// quedaba un scroll horizontal en el que había que arrastrar para llegar justo a lo único que hay
// que hacer aquí, que es decidir. En pantalla estrecha cada paciente es una TARJETA con su foto, su
// teléfono y sus botones a lo ancho. Es el mismo patrón que ya usa CalendarioCirugias (una vista por
// tamaño, con useIsMobile), no un invento nuevo.
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../services/api'
import { useToast } from '../context/ToastContext'
import { confirmar } from '../context/DialogosContext'
import { useIsMobile } from '../hooks/useIsMobile'
import FotoPaciente from './FotoPaciente'
import { normalizarTelefono, CONVENCION_TELEFONO } from '../utils/telefono'
import { llamarTelefono } from '../utils/llamar'   // B-2233 · el botón «Llamar» de siempre: marca y deja el aviso en su WhatsApp
import { Pill, Boton, Ico } from './ui'
import { t, tx, marca, tDato } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// El prefijo internacional que espera wa.me. Sale de la convención del país, no escrito aquí: el día
// que la app se venda fuera se toca CONVENCION_TELEFONO y esto sigue funcionando.
const PREFIJO_WA = CONVENCION_TELEFONO.prefijoInternacional

// Los días de la semana en la convención de Postgres (EXTRACT(DOW)): 0=domingo … 6=sábado. Se
// escriben aquí porque son nombres de días, no datos de ninguna clínica.
export const DIAS_SEMANA = [marca('components.RepasoUrgencias.domingo', 'domingo'), marca('components.RepasoUrgencias.lunes', 'lunes'), marca('components.RepasoUrgencias.martes', 'martes'), marca('components.RepasoUrgencias.miercoles', 'miércoles'), marca('components.RepasoUrgencias.jueves', 'jueves'), marca('components.RepasoUrgencias.viernes', 'viernes'), marca('components.RepasoUrgencias.sabado', 'sábado')]

export function useRepaso({ enabled = true } = {}) {
  return useQuery({
    queryKey: ['urgencias-repaso'],
    queryFn: () => api.get('/urgencias/repaso').then(r => r.data),
    refetchInterval: 60000,
    enabled,
    placeholderData: (prev) => prev,
  })
}

export function useRepasoEstado(enabled = true) {
  return useQuery({
    queryKey: ['urgencias-repaso-estado'],
    queryFn: () => api.get('/urgencias/repaso/estado').then(r => r.data).catch(() => null),
    refetchInterval: 60000,
    enabled,
    placeholderData: (prev) => prev,
  })
}

// Fecha 'YYYY-MM-DD' → '5 de octubre'. Se parte la cadena a mano: construir un Date con ella la
// interpreta en UTC y en Europe/Madrid puede pintar el día anterior.
const MESES = [marca('components.RepasoUrgencias.enero', 'enero'), marca('components.RepasoUrgencias.febrero', 'febrero'), marca('components.RepasoUrgencias.marzo', 'marzo'), marca('components.RepasoUrgencias.abril', 'abril'), marca('components.RepasoUrgencias.mayo', 'mayo'), marca('components.RepasoUrgencias.junio', 'junio'), marca('components.RepasoUrgencias.julio', 'julio'), marca('components.RepasoUrgencias.agosto', 'agosto'), marca('components.RepasoUrgencias.septiembre', 'septiembre'), marca('components.RepasoUrgencias.octubre', 'octubre'), marca('components.RepasoUrgencias.noviembre', 'noviembre'), marca('components.RepasoUrgencias.diciembre', 'diciembre')]
export function fechaLarga(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}/.test(ymd)) return ''
  const [, m, d] = ymd.split('-')
  return t('components.RepasoUrgencias.de', '{{parseInt}} de {{x}}', { parseInt: parseInt(d, 10), x: tDato(MESES[parseInt(m, 10) - 1]) || '' })
}

// El nombre para MOSTRAR: "Nombre Apellidos" (norma de la casa). El servidor ya manda el compuesto;
// las dos partes sueltas hacen falta para las iniciales del avatar.
const nombreDe = (p) => p.paciente || [p.nombre, p.apellidos].filter(Boolean).join(' ').trim() || ('Paciente ' + p.id)

// ── La identidad: foto + nombre, y las dos cosas abren algo ────────────────────────────────────
//
// La foto va con FotoPaciente, el MISMO componente que la usa en el buscador, en la ficha y en la
// conversación: la pide cifrada con el JWT y la pinta desde un blob, nunca como <img src="/api/…">.
// En modo `mini` usa el endpoint que NO registra en la auditoría de accesos (esto es un listado, y
// 20 caras serían 20 registros que ahogarían justo la señal que ese log debe dar), pero SÍ se deja
// ampliar al pulsarla: es la misma imagen, solo que grande.
function IdentidadPaciente({ p, tam = 40, onAbrirFicha }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <FotoPaciente pacienteId={p.id} nombre={p.nombre} apellidos={p.apellidos}
        tieneFoto={p.tiene_foto} noQuiereFoto={p.foto_no_deseada === true} size={tam} mini ampliable />
      <div style={{ minWidth: 0 }}>
        <button type="button" onClick={onAbrirFicha}
          style={{ background: 'none', border: 'none', padding: 0, color: 'var(--text-primary)', fontWeight: 700, fontSize: 13.5, cursor: 'pointer', textAlign: 'left', overflowWrap: 'anywhere' }}>
          {nombreDe(p)}
        </button>
        {p.num_pac ? <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{tx('components.RepasoUrgencias.n', 'Nº {{num_pac}}', { num_pac: p.num_pac })}</div> : null}
      </div>
    </div>
  )
}

// ── Cómo se le escribe o se le llama, sin salir del repaso ─────────────────────────────────────
//
// Con conversación en la app se enlaza a /whatsapp?conv=ID, que es el mecanismo que ya usa la ficha
// del paciente. Sin ella se sale a WhatsApp con su número: el repaso se hace el primer día que se
// abre después de la guardia y con quince personas por delante, así que mandar a recepción a buscar
// la ficha para copiar el teléfono es exactamente donde se abandona la lista. Solo se apaga cuando
// no hay ni conversación ni teléfono — y entonces dice por qué.
function BotonChat({ p, conMotivo }) {
  const navigate = useNavigate()
  const toast = useToast()
  const hay = !!p.conversacion_id
  // ACCESO DIRECTO, tenga o no conversación en la app. Este repaso se hace el primer día que se abre
  // después de la guardia y con el teléfono en la mano: quien tiene conversación se atiende aquí
  // dentro, pero al que escribió por primera vez —o al que no ha escrito nunca— antes había que
  // buscarlo en su ficha, copiar el número y salir de la pantalla. Justo el día que hay quince por
  // repasar. Ahora se abre WhatsApp con su número directamente.
  // DOS NÚMEROS, PORQUE SON DOS PREGUNTAS. `p.movil` es al que se puede ESCRIBIR (lo decide
  // services/movilPaciente mirando los tres campos de la ficha); `p.telefono` es el que se PINTA y
  // al que se LLAMA, y ahí un fijo es la respuesta correcta. Antes los dos salían de la misma
  // columna: a quien tenía su móvil en «Teléfono 2» esta pantalla le decía «Sin teléfono en la
  // ficha» —mentira— y a quien tiene un fijo le abría un WhatsApp que no lee nadie y se paga.
  const tel = normalizarTelefono(p.movil)
  const motivo = hay
    ? (p.conversacion_es_urgencia
        ? t('components.RepasoUrgencias.abrir_la_conversacion_por_la', 'Abrir la conversación por la que escribió el fin de semana')
        : t('components.RepasoUrgencias.abrir_su_ultima_conversacion_de', 'Abrir su última conversación de WhatsApp'))
    : (tel
        ? t('components.RepasoUrgencias.todavia_no_tiene_conversacion_aqui', 'Todavía no tiene conversación aquí: se abre WhatsApp con su número')
        // El PORQUÉ lo dice el servidor (motivo_sin_movil): «no tiene ningún teléfono» y «el que
        // tiene es un fijo» son problemas distintos y los arregla gente distinta.
        : (p.motivo_sin_movil || t('components.RepasoUrgencias.sin_telefono_en_la_ficha', 'Sin teléfono en la ficha: no hay a dónde escribir.')))
  const boton = (
    <Boton icon="whatsapp" disabled={!hay && !tel} title={motivo}
      onClick={() => {
        if (hay) return navigate('/comunicacion?conv=' + p.conversacion_id)
        // Sin conversación en la app se sale a WhatsApp con el número. `noopener` no es adorno: sin
        // él la pestaña nueva puede tocar la que la abrió.
        if (tel) window.open('https://wa.me/' + PREFIJO_WA + tel, '_blank', 'noopener,noreferrer')
      }}
      style={{ padding: '7px 12px', fontSize: 12.5 }}>
      {hay ? t('components.RepasoUrgencias.chat', 'Chat') : t('components.RepasoUrgencias.whatsapp', 'WhatsApp')}
    </Boton>
  )
  // LLAMAR. Lo pidió la clínica con esas palabras ("el primer día para llamar"): hay urgencias que
  // no se resuelven por escrito, y quien repasa esta lista tiene el teléfono en la mano. En el
  // ordenador el `tel:` puede no hacer nada — por eso el número va en el title y se puede leer.
  // Para LLAMAR vale cualquiera de los tres teléfonos de la ficha, fijo incluido: es el número que
  // marca una persona. Por eso NO cuelga de `tel` (que es el móvil para escribir).
  // B-2233 · Y SE LLAMA COMO EN EL RESTO DE LA APLICACIÓN (utils/llamar): con el programa de llamadas del equipo y
  // dejando la nota de la llamada en su conversación de WhatsApp. Antes se marcaba con un `tel:` suelto y no quedaba nada.
  const telLlamar = normalizarTelefono(p.telefono)
  const llamar = telLlamar ? (
    <Boton icon="phone" title={t('components.RepasoUrgencias.llamar_al', 'Llamar al {{telefono}}', { telefono: p.telefono })}
      onClick={() => llamarTelefono(p.telefono, toast, { conversacionId: p.conversacion_id || null, pacienteId: p.id || null, nombre: nombreDe(p) })}
      style={{ padding: '7px 12px', fontSize: 12.5 }}>
      {t('components.RepasoUrgencias.llamar', 'Llamar')}
    </Boton>
  ) : null
  // En tablet y móvil NO hay ratón, así que un `title` no lo lee nadie: el motivo va escrito al
  // lado. Un botón apagado sin explicación es otra forma de "pulso y no pasa nada".
  const par = llamar
    ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>{boton}{llamar}</span>
    : boton
  if (!conMotivo || hay || tel) return par
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {par}
      <span style={{ fontSize: 11.5, color: 'var(--text-muted)', overflowWrap: 'anywhere' }}>{motivo}</span>
    </span>
  )
}

// ── Las dos decisiones ─────────────────────────────────────────────────────────────────────────
// Un solo sitio: la tabla y la tarjeta pintan ESTO, no dos copias que se puedan desincronizar.
function AccionesRepaso({ p, puedeDecidir, soloLectura, enCurso, decidir, ancho }) {
  const estilo = { padding: '8px 12px', fontSize: 12.5, ...(ancho ? { flex: '1 1 0', justifyContent: 'center' } : null) }
  // SIN PERMISO PARA DECIDIR no se pintan dos botones apagados: se dice el estado y ya. Un botón
  // deshabilitado sin motivo es la versión visual de "pulso y no pasa nada", y aquí el motivo no es
  // que falte nada, es que a esta persona le han dado la lista para consultarla.
  if (soloLectura) {
    return p.decision
      ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: ancho ? 'flex-start' : 'flex-end' }}>
          <Pill tono={p.decision === 'sigue' ? 'verde' : 'gris'}>{p.decision === 'sigue' ? t('components.RepasoUrgencias.sigue', 'Sigue') : t('components.RepasoUrgencias.quitado', 'Quitado')}</Pill>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {p.decidido_por ? p.decidido_por : ''}{p.decidido_el ? ' · ' + p.decidido_el : ''}
          </span>
        </span>
      )
      : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('components.RepasoUrgencias.pendiente_de_repasar', 'Pendiente de repasar')}</span>
  }
  if (!p.pendiente && p.decision) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: ancho ? 'flex-start' : 'flex-end' }}>
        <Pill tono={p.decision === 'sigue' ? 'verde' : 'gris'}>
          {p.decision === 'sigue' ? t('components.RepasoUrgencias.sigue', 'Sigue') : t('components.RepasoUrgencias.quitado', 'Quitado')}
        </Pill>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {p.decidido_por ? p.decidido_por : ''}{p.decidido_el ? ' · ' + p.decidido_el : ''}
        </span>
        {/* SE PUEDE CORREGIR. Quien pulsa "quitar" por error tiene que poder deshacerlo aqui
            mismo: obligarle a ir a la ficha del paciente a volver a marcarlo es como no tenerlo. */}
        {puedeDecidir && p.decision !== 'quitado' ? (
          <Boton variante="peligro" onClick={() => decidir(p, 'quitado')} disabled={enCurso === p.id}
            style={{ padding: '6px 11px', fontSize: 12 }}>{t('components.RepasoUrgencias.quitar', 'Quitar')}</Boton>
        ) : null}
        {puedeDecidir && p.decision === 'quitado' ? (
          <Boton variante="secundario" onClick={() => decidir(p, 'sigue')} disabled={enCurso === p.id}
            style={{ padding: '6px 11px', fontSize: 12 }}>{t('components.RepasoUrgencias.volver_a_ponerlo', 'Volver a ponerlo')}</Boton>
        ) : null}
      </span>
    )
  }
  return (
    <span style={{ display: ancho ? 'flex' : 'inline-flex', gap: 8, justifyContent: 'flex-end', width: ancho ? '100%' : undefined }}>
      <Boton variante="primario" icon="check" onClick={() => decidir(p, 'sigue')}
        disabled={!puedeDecidir || enCurso === p.id} style={estilo}>
        {t('components.RepasoUrgencias.sigue_en_urgencias', 'Sigue en urgencias')}
      </Boton>
      <Boton variante="peligro" icon="x" onClick={() => decidir(p, 'quitado')}
        disabled={!puedeDecidir || enCurso === p.id} style={estilo}>
        {t('components.RepasoUrgencias.quitar', 'Quitar')}
      </Boton>
    </span>
  )
}

export function TablaRepaso({ data, compacto = false, onDecidido }) {
  const qc = useQueryClient()
  const toast = useToast()
  const navigate = useNavigate()
  const esEstrecho = useIsMobile(900)   // móvil y tablet en vertical → tarjetas
  const [enCurso, setEnCurso] = useState(null)
  const pacientes = (data && data.pacientes) || []
  // Se puede decidir si (a) hay dónde guardar la decisión (migración aplicada y ciclo calculable) y
  // (b) esta persona TIENE EL PERMISO. Lo segundo lo manda el servidor en `puede_decidir`; aquí solo
  // sirve para apagar los botones y explicarlo, nunca como barrera: el POST se vuelve a comprobar
  // allí. `!== false` para que una respuesta vieja sin el campo no apague nada.
  const puedeDecidir = !!(data && data.repaso_disponible && data.ciclo && data.puede_decidir !== false)
  const soloLectura = !!(data && data.puede_decidir === false)

  const decidir = async (p, decision) => {
    if (decision === 'quitado') {
      const ok = await confirmar({
        titulo: t('components.RepasoUrgencias.quitar_de_la_lista_de', 'Quitar de la lista de urgencias'),
        mensaje: t('components.RepasoUrgencias.dejara_de_poder_avisar_a', '{{nombreDe}} dejará de poder avisar a su doctor si escribe un día de guardia.', { nombreDe: nombreDe(p) }),
        detalle: t('components.RepasoUrgencias.se_puede_volver_a_marcar', 'Se puede volver a marcar en cualquier momento desde su ficha. La conversación que tuviera en la pestaña de urgencias sale de ahí.'),
        textoConfirmar: t('components.RepasoUrgencias.quitar_de_urgencias', 'Quitar de urgencias'),
        tipo: 'danger',
      })
      if (!ok) return
    }
    setEnCurso(p.id)
    try {
      const r = await api.post(`/urgencias/repaso/${p.id}`, { decision })
      // Se dice lo que ha pasado DE VERDAD con la conversación. Una conversación que sigue viva
      // (con mensajes sin leer, con el asistente contestando o marcada como urgente) sale de la
      // categoría pero NO se cierra, y quien pulsa tiene que saberlo.
      const c = r.data && r.data.conversaciones
      const cola = c && c.abiertas > 0
        ? t('components.RepasoUrgencias.plural_conversaciones_siguen_abiertas_te', { one: ' · {{count}} conversación sigue abierta: tenía algo sin atender', other: ' · {{count}} conversaciónes siguen abiertas: tenía algo sin atender' }, { count: c.abiertas, _forma: (c.abiertas === 1) ? 'one' : 'other' })
        : ''
      toast.success((decision === 'quitado' ? t('components.RepasoUrgencias.quitado_de_urgencias', 'Quitado de urgencias') : t('components.RepasoUrgencias.sigue_en_urgencias', 'Sigue en urgencias')) + cola)
      qc.invalidateQueries({ queryKey: ['urgencias-repaso'] })
      qc.invalidateQueries({ queryKey: ['urgencias-repaso-estado'] })
      qc.invalidateQueries({ queryKey: ['conv-contadores'] })
      qc.invalidateQueries({ queryKey: ['urgencias-estado'] })
      if (onDecidido) onDecidido()
    } catch (e) {
      toast.error((e.response && e.response.data && e.response.data.error) || t('components.RepasoUrgencias.no_se_pudo_guardar_la', 'No se pudo guardar la decisión'))
    }
    setEnCurso(null)
  }

  // La ficha se abre desde /pacientes con ?paciente=ID (no hay ruta propia por id).
  const abrirFicha = (p) => navigate('/pacientes?paciente=' + p.id)

  if (!pacientes.length) {
    return (
      <div style={{ padding: '22px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13.5 }}>
        {data && data.solo_mias
          ? t('components.RepasoUrgencias.no_tienes_ningun_paciente_en', 'No tienes ningún paciente en la lista de urgencias.')
          : t('components.RepasoUrgencias.no_hay_ningun_paciente_en', 'No hay ningún paciente en la lista de urgencias. Se marca desde su ficha o desde una cita.')}
      </div>
    )
  }

  const pie = compacto ? null : (
    <div style={{ padding: '10px 12px', fontSize: 11.5, color: 'var(--text-muted)', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
      <span style={{ flexShrink: 0, marginTop: 1 }}><Ico n="info" s={14} /></span>
      {soloLectura
        ? t('components.RepasoUrgencias.puedes_consultar_la_lista_pero', 'Puedes consultar la lista, pero no decidir en el repaso. Un administrador puede concederte ese permiso en Ajustes, en Permisos.')
        : t('components.RepasoUrgencias.sigue_en_urgencias_deja_al', '"Sigue en urgencias" deja al paciente en la lista y solo anota que ya se ha mirado en este repaso.')}
    </div>
  )

  // ── Estrecho (móvil y tablet en vertical): una tarjeta por paciente ──────────────────────────
  if (esEstrecho) {
    return (
      <div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {pacientes.map(p => (
            <div key={p.id} style={{
              borderTop: '1px solid var(--border-light)', padding: '12px 13px',
              display: 'flex', flexDirection: 'column', gap: 9,
              background: p.pendiente ? 'transparent' : 'var(--surface-2)',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                <IdentidadPaciente p={p} tam={44} onAbrirFicha={() => abrirFicha(p)} />
                {(p.escribio_finde || p.avisado_ciclo) ? <Pill tono="ambar">{t('components.RepasoUrgencias.escribio', 'Escribió')}</Pill> : null}
              </div>

              {/* Los datos, en filas etiquetadas: en una tarjeta no hay cabecera de columna que
                  diga qué es cada cosa. */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 14px', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                <span><span style={{ color: 'var(--text-muted)' }}>{t('components.RepasoUrgencias.doctor', 'Doctor: ')}</span>{p.doctor || (p.id_usu ? t('components.RepasoUrgencias.agenda', 'Agenda {{id_usu}}', { id_usu: p.id_usu }) : '—')}</span>
                <span><span style={{ color: 'var(--text-muted)' }}>{t('components.RepasoUrgencias.telefono', 'Teléfono: ')}</span>{p.telefono || '—'}</span>
                <span><span style={{ color: 'var(--text-muted)' }}>{t('components.RepasoUrgencias.ultimo_aviso', 'Último aviso: ')}</span>{p.ultimo_aviso || t('components.RepasoUrgencias.nunca', 'Nunca')}</span>
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <BotonChat p={p} conMotivo />
              </div>

              <AccionesRepaso p={p} puedeDecidir={puedeDecidir} soloLectura={soloLectura} enCurso={enCurso} decidir={decidir} ancho />
            </div>
          ))}
        </div>
        {pie}
      </div>
    )
  }

  // ── Ancho: la tabla de siempre ───────────────────────────────────────────────────────────────
  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 760 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: 11.5, fontWeight: 700 }}>
              <th style={{ padding: '8px 10px' }}>{t('components.RepasoUrgencias.paciente', 'Paciente')}</th>
              <th style={{ padding: '8px 10px' }}>{t('components.RepasoUrgencias.telefono_2', 'Teléfono')}</th>
              <th style={{ padding: '8px 10px' }}>{t('components.RepasoUrgencias.doctor_2', 'Doctor')}</th>
              <th style={{ padding: '8px 10px' }}>{t('components.RepasoUrgencias.ultimo_aviso_2', 'Último aviso')}</th>
              <th style={{ padding: '8px 10px' }}>{t('components.RepasoUrgencias.este_fin_de_semana', 'Este fin de semana')}</th>
              <th style={{ padding: '8px 10px', textAlign: 'right' }}>{t('components.RepasoUrgencias.repaso', 'Repaso')}</th>
            </tr>
          </thead>
          <tbody>
            {pacientes.map(p => (
              <tr key={p.id} style={{ borderTop: '1px solid var(--border-light)', background: p.pendiente ? 'transparent' : 'var(--surface-2)' }}>
                <td style={{ padding: '9px 10px' }}>
                  <IdentidadPaciente p={p} onAbrirFicha={() => abrirFicha(p)} />
                </td>
                <td style={{ padding: '9px 10px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{p.telefono || '—'}</td>
                <td style={{ padding: '9px 10px', color: 'var(--text-secondary)' }}>{p.doctor || (p.id_usu ? t('components.RepasoUrgencias.agenda', 'Agenda {{id_usu}}', { id_usu: p.id_usu }) : '—')}</td>
                <td style={{ padding: '9px 10px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{p.ultimo_aviso || t('components.RepasoUrgencias.nunca', 'Nunca')}</td>
                <td style={{ padding: '9px 10px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {p.escribio_finde || p.avisado_ciclo
                      ? <Pill tono="ambar">{t('components.RepasoUrgencias.escribio', 'Escribió')}</Pill>
                      : <span style={{ color: 'var(--text-muted)' }}>{t('components.RepasoUrgencias.no', 'No')}</span>}
                    <BotonChat p={p} />
                  </span>
                </td>
                <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                  <AccionesRepaso p={p} puedeDecidir={puedeDecidir} soloLectura={soloLectura} enCurso={enCurso} decidir={decidir} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pie}
    </div>
  )
}

export default TablaRepaso
